import {
  ColDef,
  GridApi,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from 'ag-grid-enterprise';
import { uniqBy } from 'lodash';
import {
  APIComponentAttributes,
  BooleanOperator,
  InventoryHierarchyMode,
  type QueryBuilderSubquery,
  ReportColumn,
} from '@ardoq/api-types';
import { logError } from '@ardoq/sentry';
import { AG_GRID_SELECTION_COLUMN_ID } from './columns/selectAndDeleteColumnDefinition';
import { columnOperations } from './columns/columnOperations';
import { mapFilterModelToSubquery } from './filters/mapFilterModelToSubquery';
import { FilterModel } from './filters/types';
import {
  InventoryContext,
  NextInventoryRowsBatchParams,
  RequestableColumnDefinition,
} from './types';
import { INVENTORY_PAGE_SIZE } from './inventoryConsts';
import { hierarchyInAgGridOperations } from './hierarchy/hierarchyInAgGridOperations';

const getSortingFromAgGridParams = (
  requestParams: IServerSideGetRowsRequest,
  columnDefs: ColDef<APIComponentAttributes>[]
) => {
  const agGridSort = requestParams.sortModel[0];
  if (!agGridSort) {
    return undefined;
  }

  return columnOperations.toSorting(
    columnDefs,
    agGridSort.colId,
    agGridSort.sort
  );
};

type RequestedColumn = Pick<
  ReportColumn,
  'key' | 'label' | 'type' | 'dataType'
>;

const toRequestedColumn = ({
  field,
  context,
  headerName,
}: RequestableColumnDefinition): RequestedColumn => ({
  label:
    context.inventoryColumnType === 'reference'
      ? context.referenceTypeName
      : (headerName ?? ''),
  key: field,
  type: context.reportColumnType,
  dataType: context.reportDataType,
});

const isColumnDefMissingBackingField = (
  colDef: ColDef<APIComponentAttributes>
): boolean =>
  colDef.context !== undefined &&
  !colDef.context.isExtraColumn &&
  colDef.field === undefined;

const logColumnDefsMissingBackingField = (
  colDefs: ColDef<APIComponentAttributes>[]
): void =>
  colDefs
    .filter(isColumnDefMissingBackingField)
    .forEach(colDef =>
      logError(
        new Error(
          `Inventory column "${colDef.colId}" carries Ardoq column metadata but no backing field, and was left out of the row request.`
        )
      )
    );

export const getDisplayedColumnsFromGridApi = (
  gridApi: GridApi
): RequestedColumn[] => {
  const colDefs = gridApi
    .getAllDisplayedColumns()
    .filter(col => col.getColId() !== AG_GRID_SELECTION_COLUMN_ID)
    .map(col => col.getColDef());

  logColumnDefsMissingBackingField(colDefs);

  return uniqBy(
    colDefs
      .filter(columnOperations.isRequestableColumnDef)
      .map(toRequestedColumn),
    column => column.key
  );
};

type RowsRequestFilterArgs = {
  gridSubquery: QueryBuilderSubquery;
  externalSubquery: QueryBuilderSubquery | null;
  hierarchyState: InventoryContext['hierarchy'];
  filterModel: FilterModel;
};

export const toRowsRequestFilter = ({
  gridSubquery,
  externalSubquery,
  hierarchyState,
  filterModel,
}: RowsRequestFilterArgs): Pick<
  NextInventoryRowsBatchParams,
  'subquery' | 'hierarchy'
> => ({
  subquery: externalSubquery
    ? {
        condition: BooleanOperator.AND,
        rules: [gridSubquery, externalSubquery],
      }
    : gridSubquery,
  hierarchy: toHierarchyMode(gridSubquery, hierarchyState, filterModel),
});

export const mapAgGridParamsToArdoqFormat = (
  params: IServerSideGetRowsParams
): NextInventoryRowsBatchParams => {
  const { request: getRowsRequestParams, api: agGridApi } = params;
  const { filterModel, startRow, endRow, groupKeys } = getRowsRequestParams;

  const columnDefs = columnOperations.toLeafColumnDefs(
    agGridApi.getColumnDefs() ?? []
  );
  const { hierarchy: hierarchyState, externalSubquery } =
    agGridApi.getGridOption('context') as InventoryContext;

  const sorting = getSortingFromAgGridParams(getRowsRequestParams, columnDefs);
  const gridSubquery = mapFilterModelToSubquery(
    filterModel as FilterModel,
    columnDefs
  );
  const columns = getDisplayedColumnsFromGridApi(agGridApi);

  return {
    from: startRow ?? 0,
    size: (endRow ?? INVENTORY_PAGE_SIZE) - (startRow ?? 0),
    sorting,
    columns,
    ...toRowsRequestFilter({
      gridSubquery,
      externalSubquery,
      hierarchyState,
      filterModel: filterModel as FilterModel,
    }),
    parentsPath: groupKeys,
  };
};

/**
 * No filters, or "Collapse All" is active, both mean a lazy, one-level
 * fetch (`hierarchyCollapsed`); otherwise, with hierarchy on and filters
 * applied, it's the eager whole-tree fetch (`hierarchyExpanded`).
 */
export const toHierarchyMode = (
  subquery: NextInventoryRowsBatchParams['subquery'],
  hierarchyState: InventoryContext['hierarchy'],
  filterModel: FilterModel
): InventoryHierarchyMode => {
  if (!hierarchyState.isHierarchyEnabled) return 'none';
  const isLazy =
    subquery.rules.length === 0 ||
    hierarchyInAgGridOperations.isHierarchyCollapsedMode(
      hierarchyState,
      filterModel
    );
  return isLazy ? 'hierarchyCollapsed' : 'hierarchyExpanded';
};
